/**
 * Trending Games Hook
 *
 * Fetches the dynamic trending games list, but only when the settings response
 * actually includes the TRENDING_GAMES section — operators without it don't pay
 * for an unused network round-trip. The returned data is consumed by
 * `useInitializeLobby` to populate the section.
 */

import { useQuery } from '@tanstack/react-query';
import { SECTION_NAMES } from '@repo/common';
import { fetchTrendingGames } from '../../../core/api/endpoints/trending.api';
import type { ITrendingGamesResponse } from '../../../core/api/endpoints/trending.api';
import type ISectionSettings from '../../../types/models/ISectionSettings';

export const useTrendingGames = (
  apiHost: string,
  sections: ISectionSettings[] | undefined,
  enabled: boolean
) => {
  const hasTrendingSection = Boolean(
    sections?.some((s) => s.name === SECTION_NAMES.TRENDING_GAMES)
  );

  return useQuery<ITrendingGamesResponse>({
    queryKey: ['trendingGames', apiHost],
    queryFn: () => fetchTrendingGames(apiHost),
    enabled: enabled && hasTrendingSection,
    staleTime: 10 * 60 * 1000,
    retry: false,
    networkMode: 'online',
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};
